import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import {
  BillingLineSide,
  BillingState,
  InvoiceCategoryFilter,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isString, uniq } from 'lodash-es';
import { useContext, useEffect, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { Invoice } from '~hooks/useInvoices/models';
import useInvoices from '~hooks/useInvoices/useInvoices';
import SettlementInvoiceEditDialog from '~pages/Approvals/DriverPay/Settlements/SettlementDetails/SettlementLines/SettlementInvoiceEditDialog';
import { useStores } from '~store';
import isEnumValue from '~utils/isEnumValue';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ChargesBillingLinesQueryContext from '../ChargesBillingLinesQueryContext';
import { getLineSettlementNumber } from '../groupBillingLines';

const titleKeys = {
  [BillingLineSide.PAYABLE]: 'approvals.approve_charges.actions.edit_payable',
  [BillingLineSide.RECEIVABLE]: 'approvals.approve_charges.actions.edit_receivable',
} satisfies Record<BillingLineSide, string>;

const rateCategoryBySide = {
  [BillingLineSide.PAYABLE]: InvoiceCategoryFilter.PAYABLES,
  [BillingLineSide.RECEIVABLE]: InvoiceCategoryFilter.RECEIVABLES,
} satisfies Record<BillingLineSide, InvoiceCategoryFilter>;

const isLockedByBillingState = {
  [BillingState.APPROVED]: true,
  [BillingState.DRAFT]: false,
  [BillingState.NOT_APPLIED]: false,
  [BillingState.PAID]: true,
  [BillingState.REJECTED]: false,
  [BillingState.UNSETTLED]: false,
} satisfies Record<BillingState, boolean>;

interface ChargesInvoiceEditDialogProps {
  jobId: string;
  onClose: () => void;
  side: BillingLineSide;
}

export default function ChargesInvoiceEditDialog({
  jobId,
  onClose,
  side,
}: ChargesInvoiceEditDialogProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { getJobPayable, getJobReceivable } = useInvoices();
  // the invoice hook is rebuilt on every render, so the effect below reads it from here
  const readInvoiceRef = useRef(
    side === BillingLineSide.PAYABLE ? getJobPayable : getJobReceivable,
  );
  readInvoiceRef.current =
    side === BillingLineSide.PAYABLE ? getJobPayable : getJobReceivable;
  // undefined while reading; null when the job has no invoice on this side yet
  const [invoice, setInvoice] = useState<Invoice | null | undefined>(undefined);
  const [readCount, setReadCount] = useState(0);
  const [hasReadFailed, setHasReadFailed] = useState(false);
  const { chargesByJobId } = useContext(ChargesBillingLinesQueryContext);
  const sideLines = (chargesByJobId.get(jobId)?.lines ?? []).filter(
    (line) => line.side === side && !line.isRejected,
  );
  const lockedLines = sideLines.filter(
    (line) =>
      line.billingState !== null &&
      isEnumValue(BillingState, line.billingState) &&
      isLockedByBillingState[line.billingState],
  );
  const lockedSettlementNumbers = uniq(
    lockedLines.map(getLineSettlementNumber).filter(isString),
  ).join(', ');
  const lockNotice =
    lockedLines.length === 0 ? null : (
      <Alert data-test-id="charges-lock-notice" severity="info" sx={{ mb: 2 }}>
        {lockedLines.length === sideLines.length
          ? t('approvals.approve_charges.edit_dialog.locked_all', {
              settlements: lockedSettlementNumbers,
            })
          : t('approvals.approve_charges.edit_dialog.locked_some', {
              count: lockedLines.length,
              settlements: lockedSettlementNumbers,
              total: sideLines.length,
            })}
      </Alert>
    );

  useEffect(() => {
    const controller = new AbortController();

    const loadInvoice = async () => {
      setHasReadFailed(false);
      try {
        const readInvoice = await readInvoiceRef.current(jobId, companyId, {
          signal: controller.signal,
        });

        if (!controller.signal.aborted) {
          setInvoice(readInvoice ?? null);
        }
      } catch {
        if (!controller.signal.aborted) {
          setHasReadFailed(true);
        }
      }
    };

    void loadInvoice();

    return () => controller.abort();
  }, [jobId, companyId, side, readCount]);

  const retryRead = () => setReadCount((count) => count + 1);

  if (invoice) {
    return (
      <SettlementInvoiceEditDialog
        invoice={invoice}
        isSettlementApproved={
          sideLines.length > 0 && lockedLines.length === sideLines.length
        }
        notice={
          <>
            {hasReadFailed && (
              <Alert
                action={
                  <SmallButton onClick={retryRead} variant="outlined">
                    {t('actions.retry')}
                  </SmallButton>
                }
                data-test-id="charges-read-failed-notice"
                severity="error"
                sx={{ mb: 2 }}
              >
                {t('approvals.approve_charges.edit_dialog.read_failed')}
              </Alert>
            )}
            {lockNotice}
          </>
        }
        onClose={onClose}
        onRateChanged={() => setReadCount((count) => count + 1)}
        rateCategory={rateCategoryBySide[side]}
      />
    );
  }

  return (
    <Dialog isOpen maxWidth="sm" onClose={onClose}>
      <DialogHeader onClose={onClose}>{t(titleKeys[side])}</DialogHeader>
      <DialogContent>
        {invoice === undefined && hasReadFailed ? (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography sx={{ color: 'error.main' }} variant="body2">
              {t('approvals.approve_charges.edit_dialog.read_failed')}
            </Typography>
            <SmallButton onClick={retryRead} variant="outlined">
              {t('actions.retry')}
            </SmallButton>
          </Box>
        ) : invoice === undefined ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Typography variant="body2">
            {t('approvals.approve_charges.edit_dialog.no_invoice')}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
